'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../model/auth.store';
import { LoadingSpinner } from '@/shared/ui';
import { useAuthActions, useLoginLoading } from '../model/auth.selectors';
import { resolvePostLoginDestination } from '../lib/post-login-destination';
import { isSafeRedirectPath, withRedirectParam } from '../lib/safe-redirect';
import { resolveAuthErrorMessage } from '../lib/auth-error-message';
import {
  consumeOAuthFailureMessage,
  LOGIN_EMAIL_KEY,
  OAUTH_FAILED_CODE,
} from '../lib/oauth-failure';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { User } from '@/types/user';
import { Input } from '@/shared/ui/shadcn/input';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { ApiClientError } from '@/shared/lib/api';
import { resolveApiUrl } from '@/shared/lib/api-host';
import { Loader2 } from 'lucide-react';
import { Logo } from '@/shared/ui/brand/Logo';
import { getAppleOAuthUrl } from '../lib/apple-oauth';
import { startVkIdLogin } from '../lib/vk-id-oauth';
import { useAuthProviders } from '../model/use-auth-providers';
import { AppleLogoIcon, GoogleLogoIcon, VkIdLogoIcon } from './social-icons';
import {
  APPLE_OAUTH_BUTTON,
  GOOGLE_OAUTH_BUTTON,
  VK_OAUTH_BUTTON,
} from './oauth-button-classes';

function createLoginSchema(tValid: ReturnType<typeof useTranslations>) {
  return z.object({
    email: z
      .string()
      .min(1, tValid('required'))
      .email(tValid('email')),
    password: z.string().optional(),
  });
}

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

export function LoginForm() {
  const t = useTranslations('auth.login');
  const tValid = useTranslations('common.errors.validation');
  const tErrors = useTranslations('common.errors');
  const errorFallbacks = {
    network: tErrors('network'),
    generic: tErrors('generic'),
  };
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentLocale = useLocale();
  const { login, getGoogleOAuthUrl } = useAuthActions();
  const { isVkIdAvailable } = useAuthProviders();
  const isLoggingIn = useLoginLoading();
  const queryClient = useQueryClient();
  const [authMethods, setAuthMethods] = useState<string[]>([]);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [isEmailFormExpanded, setIsEmailFormExpanded] = useState(false);

  const loginSchema = useMemo(() => createLoginSchema(tValid), [tValid]);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const email = watch('email') ?? '';

  // Get redirect URL from search params (validated to prevent open redirect)
  const redirectUrl = isSafeRedirectPath(searchParams.get('redirect'))
    ? searchParams.get('redirect')
    : null;

  // ADR-001: links (landing CTA, bookmarks) land here, but the browser is
  // tried as a sign-in method first; this form is only for when the server
  // declines. Once that opens an account, continue into the app.
  const deviceSignInPending = useAuthStore((state) => state.deviceSignInPending);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  useEffect(() => {
    if (isAuthenticated && !isLoggingIn) {
      router.replace(redirectUrl ?? '/');
    }
  }, [isAuthenticated, isLoggingIn, redirectUrl, router]);

  // Load saved email on mount (from sessionStorage for security)
  useEffect(() => {
    const savedEmail = sessionStorage.getItem(LOGIN_EMAIL_KEY);
    if (savedEmail) {
      setValue('email', savedEmail);
    }
  }, [setValue]);

  // `?email=` only pre-fills the form. It used to sign the current user out
  // when the address differed, which let any external link end someone's
  // session (a CSRF logout); a signed-in visitor is now sent on by the auth
  // layout like on any other login visit, and switching accounts takes an
  // explicit sign-out.
  //
  // `?error=` is honoured only as the fixed OAuth failure code. The text shown
  // is the API's explanation handed over through sessionStorage, or a
  // localized generic line, never words taken from the URL (a crafted link
  // could otherwise print anything on the real sign-in page).
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const errorParam = searchParams.get('error');

    if (emailParam) {
      setValue('email', emailParam);
    }

    if (errorParam === OAUTH_FAILED_CODE) {
      setError('root', {
        message: consumeOAuthFailureMessage() ?? t('oauthFailed'),
      });
    }

    if (emailParam || errorParam) {
      // Clean the address bar without adding history. Keep the remaining
      // params: dropping them destroyed the ?redirect= deep link after a
      // failed OAuth round-trip.
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      url.searchParams.delete('email');
      router.replace(url.pathname + url.search, { scroll: false });
    }
  }, [searchParams, router, setError, setValue, t]);

  // Save only email when it changes (not password for security)
  useEffect(() => {
    if (email) {
      sessionStorage.setItem(LOGIN_EMAIL_KEY, email);
    }
  }, [email]);

  const onSubmit = async (data: LoginFormData) => {
    clearErrors('root');

    // OAuth-only accounts have no password to submit — but the form is still
    // live, so Enter in the email field used to fire a real login with an
    // empty password (a guaranteed 401 that burns the login throttle).
    if (isOAuthOnly) {
      return;
    }

    // The schema keeps password optional because OAuth-only accounts never see
    // the field. When it IS shown, an empty submit used to travel to the API
    // and come back as a raw payload-path message, so require it here instead.
    if (showPasswordField && !data.password) {
      setError('password', { message: tValid('required') });

      return;
    }

    try {
      await login({
        email: data.email,
        password: data.password ?? '',
        locale: currentLocale,
      });
      // Clear saved email only on successful login
      sessionStorage.removeItem(LOGIN_EMAIL_KEY);
      // Redirect based on verification status
      const { pendingEmailVerification } = useAuthStore.getState();
      const user = queryClient.getQueryData<User>(cacheKeys.user) ?? null;
      if (pendingEmailVerification) {
        router.push(withRedirectParam('/auth/verify-email', redirectUrl));
      } else {
        const destination = await resolvePostLoginDestination(user, {
          queryClient,
          redirectUrl,
        });
        router.push(destination);
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        // Extract field-specific errors
        const fieldErrors = error.getFieldErrors();
        const keys = Object.keys(fieldErrors);
        if (keys.length > 0) {
          for (const key of keys) {
            if (key === 'email' || key === 'password') {
              setError(key, { message: fieldErrors[key] });
            }
          }
          if (!keys.includes('email') && !keys.includes('password')) {
            const firstKey = keys[0];
            setError('root', {
              message:
                (firstKey ? fieldErrors[firstKey] : undefined) ??
                error.getFirstError(),
            });
          }
        } else {
          setError('root', {
            message: resolveAuthErrorMessage(error, errorFallbacks),
          });
        }
      } else {
        setError('root', {
          message: resolveAuthErrorMessage(error, errorFallbacks),
        });
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Store redirect URL in sessionStorage for OAuth callback to use
      if (redirectUrl) {
        sessionStorage.setItem('oauth_redirect', redirectUrl);
      }
      const oauthUrl = await getGoogleOAuthUrl();
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Google OAuth error:', error);
      setError('root', {
        message: resolveAuthErrorMessage(error, errorFallbacks),
      });
    }
  };

  const handleVkLogin = async () => {
    try {
      if (redirectUrl) {
        sessionStorage.setItem('oauth_redirect', redirectUrl);
      }
      await startVkIdLogin();
    } catch (error) {
      console.error('VK ID error:', error);
      setError('root', {
        message: resolveAuthErrorMessage(error, errorFallbacks),
      });
    }
  };

  const handleAppleLogin = () => {
    try {
      if (redirectUrl) {
        sessionStorage.setItem('oauth_redirect', redirectUrl);
      }
      window.location.href = getAppleOAuthUrl();
    } catch (error) {
      console.error('Apple OAuth error:', error);
      setError('root', {
        message: resolveAuthErrorMessage(error, errorFallbacks),
      });
    }
  };

  // Check auth methods when email loses focus
  const checkAuthMethods = useCallback(async () => {
    if (!email || !email.includes('@')) return;

    setIsCheckingAuth(true);
    // Don't reset hasCheckedAuth to avoid flickering

    try {
      const response = await fetch(
        `${resolveApiUrl()}/api/v1/auth/check-methods`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
          },
          body: JSON.stringify({
            data: {
              type: 'auth-check',
              attributes: {
                email: email.toLowerCase(),
              },
            },
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const methods = data.data?.attributes?.methods || [];
        setAuthMethods(methods);
        setHasCheckedAuth(true);

        // If user only has OAuth, clear password field
        if (methods.length > 0 && !methods.includes('password')) {
          setValue('password', '');
        }
      }
    } catch (error) {
      console.error('Error checking auth methods:', error);
      // Fail silently, show both options
      setAuthMethods([]);
    } finally {
      setIsCheckingAuth(false);
    }
  }, [email, setValue]);

  const handleEmailBlur = () => {
    checkAuthMethods();
  };

  // Helper to determine what auth UI to show
  // Show password field by default or if auth check hasn't completed, to avoid flickering
  const showPasswordField =
    !hasCheckedAuth ||
    authMethods.length === 0 ||
    authMethods.includes('password');
  const isOAuthOnly =
    hasCheckedAuth &&
    authMethods.length > 0 &&
    !authMethods.includes('password');
  const hasGoogleMethod = authMethods.includes('google');
  const hasAppleMethod = authMethods.includes('apple');
  const hasVkMethod = authMethods.includes('vk');

  // Auto-expand email form when there's a general error or pre-filled email
  // so users see the context they need to act on (e.g. OAuth failure, logout notice).
  useEffect(() => {
    if (errors.root?.message && !isEmailFormExpanded) {
      setIsEmailFormExpanded(true);
    }
  }, [errors.root, isEmailFormExpanded]);

  const generalError = errors.root?.message;

  if (deviceSignInPending || (isAuthenticated && !isLoggingIn)) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-center mb-8">
        <span className="block dark:hidden">
          <Logo variant="white" />
        </span>
        <span className="hidden dark:block">
          <Logo variant="bright" />
        </span>
      </div>
      <div className="w-full space-y-4">
          {generalError && (
            <NoticeBanner variant="error" message={generalError} />
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              className={APPLE_OAUTH_BUTTON}
              onClick={handleAppleLogin}
            >
              <AppleLogoIcon />
              {t('appleButton')}
            </Button>
            {isVkIdAvailable && (
              <Button
                type="button"
                className={VK_OAUTH_BUTTON}
                onClick={handleVkLogin}
              >
                <VkIdLogoIcon />
                {t('vkButton')}
              </Button>
            )}
            <Button
              type="button"
              className={GOOGLE_OAUTH_BUTTON}
              onClick={handleGoogleLogin}
            >
              <GoogleLogoIcon />
              {t('googleButton')}
            </Button>
          </div>

          {/* "Use email instead" link — one-way: shown only until first click, then hidden to save space. */}
          {!isEmailFormExpanded && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setIsEmailFormExpanded(true)}
                className="text-list-item-subtitle text-auth-panel-foreground/90 underline underline-offset-2 hover:text-auth-panel-foreground transition-colors"
                aria-expanded={isEmailFormExpanded}
                aria-controls="email-login-form"
              >
                {t('useEmailInstead')}
              </button>
            </div>
          )}

          <div
            id="email-login-form"
            className={`grid transition-all duration-300 ease-in-out ${
              isEmailFormExpanded
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0'
            }`}
            aria-hidden={!isEmailFormExpanded}
          >
            <div className="overflow-hidden">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-auth-panel-foreground/30 dark:border-input" />
                  </div>
                  <div className="relative flex justify-center text-section-eyebrow">
                    {/* The chip masks the rule behind it, so its background has
                        to match the page: teal in light, neutral in dark. */}
                    <span className="bg-primary px-2 text-auth-panel-foreground dark:bg-background dark:text-muted-foreground">
                      {t('continueWithEmail')}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-auth-panel-foreground">
                    {t('email')}
                    {isCheckingAuth && (
                      <span className="ms-2 text-card-meta text-auth-panel-foreground/80">
                        <Loader2 className="inline h-3 w-3 animate-spin" />
                      </span>
                    )}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    {...register('email', { onBlur: handleEmailBlur })}
                    className={errors.email ? 'border-destructive-on-primary' : ''}
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  />
                  {errors.email && (
                    <p className="text-form-error text-destructive-on-primary">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {isOAuthOnly && (
                  <NoticeBanner variant="info" message={t('oauthOnlyMessage')} />
                )}

                {showPasswordField && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-auth-panel-foreground">{t('password')}</Label>
                    <PasswordInput
                      id="password"
                      autoComplete="current-password"
                      {...register('password')}
                      className={
                        errors.password
                          ? 'border-destructive-on-primary'
                          : ''
                      }
                      tabIndex={isEmailFormExpanded ? 0 : -1}
                    />
                    {errors.password && (
                      <p className="text-form-error text-destructive-on-primary">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                )}

                {showPasswordField && (
                  <div className="flex justify-end">
                    <Link
                      href="/auth/forgot-password"
                      className="text-list-item-value text-auth-panel-foreground hover:text-auth-panel-foreground/80"
                      tabIndex={isEmailFormExpanded ? 0 : -1}
                    >
                      {t('forgotPassword')}
                    </Link>
                  </div>
                )}

                {!isOAuthOnly && (
                  <Button
                    type="submit"
                    disabled={isLoggingIn}
                    className="w-full h-12 rounded-full bg-auth-panel-foreground text-primary hover:bg-auth-panel-foreground/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  >
                    {isLoggingIn ? (
                      <>
                        <Loader2 className="me-2 h-4 w-4 animate-spin" />
                        {t('signingIn')}
                      </>
                    ) : (
                      t('submit')
                    )}
                  </Button>
                )}

                {isOAuthOnly && hasAppleMethod && (
                  <Button
                    type="button"
                    onClick={handleAppleLogin}
                    className={APPLE_OAUTH_BUTTON}
                    size="lg"
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  >
                    <AppleLogoIcon />
                    {t('oauthOnlyAppleButton')}
                  </Button>
                )}
                {isOAuthOnly && hasVkMethod && (
                  <Button
                    type="button"
                    onClick={handleVkLogin}
                    className={VK_OAUTH_BUTTON}
                    size="lg"
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  >
                    <VkIdLogoIcon />
                    {t('vkButton')}
                  </Button>
                )}
                {isOAuthOnly && hasGoogleMethod && (
                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    className={GOOGLE_OAUTH_BUTTON}
                    size="lg"
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  >
                    <GoogleLogoIcon />
                    {t('oauthOnlyButton')}
                  </Button>
                )}

                <div className="text-center text-list-item-subtitle text-auth-panel-foreground/90">
                  {t.rich('description', {
                    createAccount: (chunks) => (
                      <Link
                        // Carry the deep-link redirect across the form switch —
                        // otherwise a purchase-intent link (e.g. the landing
                        // storefront CTA) dies when the user picks "create account".
                        href={
                          redirectUrl
                            ? `/auth/register?redirect=${encodeURIComponent(redirectUrl)}`
                            : '/auth/register'
                        }
                        className="font-medium text-auth-panel-foreground underline hover:text-auth-panel-foreground/80"
                        tabIndex={isEmailFormExpanded ? 0 : -1}
                      >
                        {chunks}
                      </Link>
                    ),
                  })}
                </div>
              </form>
            </div>
          </div>
      </div>
    </div>
  );
}
